import { categoryEmoji, categoryOf, javaIsBlank, mapCaption, mapsShowing, oneLine, slugify, sortedPins, type ExportData, type ExportEntry, type ExportMap, type ExportStory } from '@realmery/realm-core';
import { entryIdOfLink, sanitizeEntryHtmlToFragment } from '../security/sanitize.ts';

/**
 * The Markdown export (docs/PARITY-STEP-1-PLAN.md section 4), as the desktop's
 * `export.MarkdownExporter` writes its folder: `index.md`, one file per entry in
 * `entries/`, each entry's pictures in `entries/images/<entry>/`, and each Story Studio
 * project as one manuscript in `stories/`, and each map as a page in `maps/` with its
 * picture flattened into a JPEG (`mapImages`, by slug, drawn by the page: Markdown can't
 * overlay anything). The page zips it.
 */
export function markdownFiles(data: ExportData, mapImages: ReadonlyMap<string, Uint8Array> = new Map()): Map<string, Uint8Array> {
  const encoder = new TextEncoder();
  const files = new Map<string, Uint8Array>();
  const fileNames = new Map<string, string>();
  const taken = new Set<string>();
  for (const entry of data.entries) {
    // `uniqueFileName`: the title's slug, then -2, -3… for the ones after.
    const base = slugify(entry.title);
    let candidate = base;
    for (let suffix = 2; taken.has(`${candidate}.md`); suffix++) candidate = `${base}-${suffix}`;
    taken.add(`${candidate}.md`);
    fileNames.set(entry.id, `${candidate}.md`);
  }

  files.set('index.md', encoder.encode(indexPage(data, fileNames)));
  for (const entry of data.entries) {
    const fileName = fileNames.get(entry.id)!;
    let md = `# ${entry.title}\n\n*${entry.typeName}*\n\n`;
    if (entry.labels.length > 0) md += `**Labels:** ${entry.labels.join(', ')}\n\n`;
    if (entry.metadata.length > 0) {
      for (const [key, value] of entry.metadata) md += `**${key}:** ${value}  \n`;
      md += '\n';
    }
    md += entry.locked ? '*🔒 Content is locked.*\n\n' : `${htmlToMarkdown(entry.contentHtml)}\n\n`;
    md += relationships(entry, fileNames);
    const onMaps = mapsShowing(data.atlas, entry.id, true);
    if (onMaps.length > 0) {
      md += '## Appears on\n\n';
      for (const map of onMaps) md += `- [🗺 ${map.map.name}](../maps/${map.slug}.md)\n`;
      md += '\n';
    }
    if (!entry.locked && entry.images.length > 0) {
      const folder = fileName.slice(0, -'.md'.length);
      md += '## Images\n\n';
      for (const image of entry.images) {
        const name = safeImageName(image.fileName, image.id);
        files.set(`entries/images/${folder}/${name}`, image.data);
        md += `![${image.fileName}](images/${folder}/${name})\n\n`;
      }
    }
    files.set(`entries/${fileName}`, encoder.encode(md));
  }
  for (const map of data.atlas.maps) {
    files.set(`maps/${map.slug}.jpg`, mapImages.get(map.slug) ?? new Uint8Array());
    files.set(`maps/${map.slug}.md`, encoder.encode(mapPage(data, map, fileNames)));
  }
  for (const story of data.stories) files.set(`stories/${story.slug}.md`, encoder.encode(storyPage(story, fileNames)));
  return files;
}

/** `writeMap`: the flattened picture, its scale, and its places with links. */
function mapPage(data: ExportData, map: ExportMap, fileNames: ReadonlyMap<string, string>): string {
  let md = `# ${map.map.name}\n\n![${map.map.name}](${map.slug}.jpg)\n\n`;
  const caption = mapCaption(map);
  if (caption !== '') md += `*${caption}*\n\n`;
  md += '## Places\n\n';
  const place = (glyph: string, label: string, categoryId: string | null, notes: string, entryId: string | null, mapId: string | null, visible: boolean) => {
    // Only an emoji reads as text; built-in and imported icons are drawn in the picture.
    const name = javaIsBlank(label) ? '(unlabeled)' : label;
    md += `- ${categoryEmoji(data.atlas, categoryId) ?? glyph} `;
    const entryFile = entryId === null ? undefined : fileNames.get(entryId);
    const target = mapId === null ? undefined : data.atlas.maps.find((m) => m.map.id === mapId);
    if (entryFile !== undefined) md += `[${name}](../entries/${entryFile})`;
    else if (target) md += `[${name}](${target.slug}.md)`;
    else md += `**${name}**`;
    const category = categoryOf(data.atlas, categoryId);
    if (category) md += ` - ${category.name}`;
    if (!visible) md += ' *(GM only)*';
    if (!javaIsBlank(notes)) md += `  \n  ${oneLine(notes)}`;
    md += '\n';
  };
  for (const pin of sortedPins(map)) place('📍', pin.label, pin.categoryId, pin.notes, pin.linkedEntryId, pin.linkedMapId, pin.visible);
  for (const region of map.regions) place('⬠', region.label, region.categoryId, region.notes, region.linkedEntryId, region.linkedMapId, region.visible);
  return md;
}

/** `writeStory`: the project as one manuscript, acts as ##, chapters as ###, scenes as ####; linked names point at their entry's file. */
function storyPage(story: ExportStory, fileNames: ReadonlyMap<string, string>): string {
  let md = `# ${story.name}\n\n[Back to index](../index.md)\n\n`;
  if (story.description.trim() !== '') md += `${story.description}\n\n`;
  md += `*${words(story.words)}*\n\n`;
  let act: string | null = null;
  let chapter: string | null = null;
  for (const scene of story.scenes) {
    if (scene.actTitle !== act) {
      act = scene.actTitle;
      chapter = null;
      md += `## ${act}\n\n`;
    }
    if (scene.chapterTitle !== chapter) {
      chapter = scene.chapterTitle;
      md += `### ${chapter}\n\n`;
    }
    md += `#### ${scene.title}\n\n`;
    const text = htmlToMarkdown(scene.contentHtml, (entryId) => {
      const file = fileNames.get(entryId);
      return file ? `../entries/${file}` : null;
    });
    if (text.trim() !== '') md += `${text}\n\n`;
  }
  return `${md.trim()}\n`;
}

/** `String.format("%,d words", n)`. */
const words = (n: number) => `${n.toLocaleString('en-US')} words`;

function indexPage(data: ExportData, fileNames: ReadonlyMap<string, string>): string {
  let md = `# ${data.realm.name}\n\n`;
  if (data.realm.description.trim() !== '') md += `${data.realm.description}\n\n`;
  if (data.atlas.maps.length > 0) {
    md += '## Maps\n\n';
    for (const map of data.atlas.maps) md += `- [${map.map.name}](maps/${map.slug}.md)\n`;
    md += '\n';
  }
  if (data.stories.length > 0) {
    md += '## Stories\n\n';
    for (const story of data.stories) md += `- [${story.name}](stories/${story.slug}.md) - ${words(story.words)}\n`;
    md += '\n';
  }
  md += '## Entries\n\n';
  for (const entry of data.entries) md += `- [${entry.title}](entries/${fileNames.get(entry.id)}) - ${entry.typeName}\n`;
  if (data.collections.length > 0) {
    md += '\n## Collections\n\n';
    for (const collection of data.collections) {
      md += `### ${collection.name}\n`;
      for (const member of collection.members) md += `- [${member.title}](entries/${fileNames.get(member.id)})\n`;
      md += '\n';
    }
  }
  return md;
}

function relationships(entry: ExportEntry, fileNames: ReadonlyMap<string, string>): string {
  if (entry.links.length === 0) return '';
  let md = '## Relationships\n\n';
  for (const link of entry.links) {
    const target = fileNames.get(link.targetEntryId);
    md += `- ${link.relationshipType}: ${target ? `[${link.targetTitle}](${target})` : link.targetTitle}\n`;
  }
  return `${md}\n`;
}

/** `safeImageName`: anything but letters, digits, `.`, `_` and `-` as `_`; the picture's id if nothing's left. */
function safeImageName(fileName: string, id: string): string {
  const name = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
  return name.trim() === '' ? id : name;
}

/**
 * `StoryExport.relink`: each mention link pointed at wherever its entry is in this export,
 * or, when the entry isn't in it, turned back into the plain name.
 */
export function relinkMentions(root: DocumentFragment, hrefFor: (entryId: string) => string | null): void {
  for (const link of [...root.querySelectorAll('a[href]')]) {
    const entryId = entryIdOfLink(link.getAttribute('href') ?? '');
    if (entryId === null) continue;
    const href = hrefFor(entryId);
    if (href === null) link.replaceWith(...link.childNodes);
    else link.setAttribute('href', href);
  }
}

/** jsoup's `TextNode.text()`: runs of whitespace, no-break spaces included, as one space. */
const normaliseWhitespace = (text: string) => text.replace(/[ \t\n\f\r\u00a0]+/g, ' ');

/**
 * The desktop's `export.HtmlContent.toMarkdown`: the entry's HTML parsed (the browser's
 * parser, as jsoup's, follows the HTML standard), its nodes turned into Markdown, blank
 * lines collapsed and the ends trimmed. The page only parses HTML through DOMPurify
 * (security/trustedTypes.ts), so this reads what the entry view would show: the same
 * Markdown for what editors write, and nothing active, such as a `javascript:` link,
 * carried into the file.
 */
export function htmlToMarkdown(html: string, relink?: (entryId: string) => string | null): string {
  const root = sanitizeEntryHtmlToFragment(html);
  if (relink) relinkMentions(root, relink);
  let out = '';
  const inline = (el: Element) => el.childNodes.forEach(render);
  function render(node: Node): void {
    if (node.nodeType === Node.TEXT_NODE) {
      out += normaliseWhitespace(node.nodeValue ?? '');
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    switch (tag) {
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6':
        out += `\n${'#'.repeat(Number(tag[1]))} `;
        inline(el);
        out += '\n\n';
        break;
      case 'p':
      case 'div':
        inline(el);
        out += '\n\n';
        break;
      case 'br':
        out += '  \n';
        break;
      case 'b':
      case 'strong':
        out += '**';
        inline(el);
        out += '**';
        break;
      case 'i':
      case 'em':
        out += '_';
        inline(el);
        out += '_';
        break;
      case 'a':
        out += '[';
        inline(el);
        out += `](${el.getAttribute('href') ?? ''})`;
        break;
      case 'ul':
        for (const li of el.children) {
          out += '- ';
          inline(li);
          out += '\n';
        }
        out += '\n';
        break;
      case 'ol': {
        let i = 1;
        for (const li of el.children) {
          out += `${i++}. `;
          inline(li);
          out += '\n';
        }
        out += '\n';
        break;
      }
      case 'script':
      case 'style':
        // jsoup holds their contents as data, not text, so they add nothing.
        break;
      case 'blockquote':
        out += '> ';
        inline(el);
        out += '\n\n';
        break;
      default:
        inline(el);
    }
  }
  root.childNodes.forEach(render);
  return out.replace(/\n{3,}/g, '\n\n').trim();
}
